import {useState, useMemo} from 'react'
import type {Board, TaskStatus} from '../../types'
import {useApp} from '../../context/AppContext'
import {generateId} from '../../utils/helpers'
import KanbanColumn from './KanbanColumn'
import KanbanCard from './KanbanCard'
import NewTaskForm from './NewTaskForm'
import type {BoardFilters} from './BoardFilters'
import {matchesTaskFilters, sortBoardTasks} from './taskFilters'

interface KanbanBoardProps {
  board: Board
  filters?: BoardFilters
}

export default function KanbanBoard({board, filters}: KanbanBoardProps) {
  const {state, getTasksForBoard} = useApp()
  const [newTaskStatus, setNewTaskStatus] = useState<TaskStatus | null>(null)
  const allTasks = getTasksForBoard(board.id)

  const tasks = useMemo(() => {
    if (!filters) return allTasks

    return allTasks.filter((task) => matchesTaskFilters(task, filters, state.currentUser?.id))
  }, [allTasks, filters, state.currentUser?.id])

  const unmappedTasks = tasks.filter(
    (task) => !board.columns.some((column) => column.status === task.status)
  )

  const handleAddTask = (status: TaskStatus) => {
    setNewTaskStatus(status)
  }

  return (
    <>
      {unmappedTasks.length > 0 && (
        <div className='mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4'>
          <h3 className='text-sm font-semibold text-amber-800'>
            Tarefas sem uma etapa configurada ({unmappedTasks.length})
          </h3>
          <p className='mb-3 mt-1 text-xs text-amber-700'>
            Escolha uma etapa no cartão para recolocar a tarefa no fluxo do quadro.
          </p>
          <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-3'>
            {unmappedTasks.map((task) => (
              <KanbanCard key={task.id} task={task} />
            ))}
          </div>
        </div>
      )}
      <div className='kanban-scroll flex gap-4 h-full pb-4'>
        {board.columns.map((column) => {
          const columnTasks = sortBoardTasks(
            tasks.filter((t) => t.status === column.status),
            filters?.sortBy || 'default'
          )
          return (
            <KanbanColumn
              key={column.id}
              column={column}
              tasks={columnTasks}
              totalCount={
                state.tasks.filter(
                  (task) => task.boardId === board.id && task.status === column.status
                ).length
              }
              onAddTask={handleAddTask}
            />
          )
        })}
      </div>

      {newTaskStatus && (
        <NewTaskForm
          boardId={board.id}
          initialStatus={newTaskStatus}
          onClose={() => setNewTaskStatus(null)}
        />
      )}
    </>
  )
}

export function QuickTaskId() {
  return `task-${generateId()}`
}
